import React from 'react';
import { Field } from '../Form';
import { renderFields, FormExtensionContext } from '../formUtils';
import { ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

function FieldGroup({
  fields,
  inputClassName,
  divider = false,
  label,
  showReset = false,
  resetPaths = [],
  className = '',
  values,
  setFieldValue,
  initialValues,
  errors,
  touched,
}) {
  const {
    customFieldPropsAreEqual,
    customLeafFieldNames,
    customContainerChildrenPropsEqual,
    customContainerValuesEqual,
  } = React.useContext(FormExtensionContext);

  if (!fields) return null;

  const handleReset = () => {
    resetPaths.forEach(path => {
      // Get the initial value for this path
      const initialValue = get(initialValues, path);
      if (initialValue !== undefined) {
        setFieldValue(path, initialValue);
      } else {
        // Set to empty object if no initial value
        setFieldValue(path, {});
      }
    });
  };

  const get = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  return (
    // `gap-y-2` is the declarative-form row gap. Keep in sync with the manual-JSX
    // counterpart `OPTION_GROUP_CLASSNAME` (Formik/components/optionGroupClass.js)
    // and Form.js `mb-2!` (isCheckboxFollowedByCheckbox) — all three resolve to
    // Tailwind `2` (8px), so any change must update every site.
    <div
      className={clsx('flex flex-col gap-y-2 gap-x-1', className, {
        'border-b border-gray-100 dark:border-border pb-4': divider,
      })}
    >
      {label && showReset && (
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-foreground">{label}</h4>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="p-1 text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-muted-foreground rounded"
              title="Reset to default"
            >
              <ArrowPathIcon className="h-3 w-3" />
            </button>
            <button
              type="button"
              className="p-1 text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-muted-foreground rounded"
              title="Show/hide element"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {renderFields({ fields, values, setFieldValue })
        .filter(Boolean)
        .map((field, index) => (
          <Field
            key={field.name || field.id || `field-${index}`}
            field={{
              ...field,
              visible: field.visible === undefined ? true : field.visible,
              noMargin: true,
            }}
            values={values}
            setFieldValue={setFieldValue}
            initialValues={initialValues}
            inputClassName={inputClassName}
            errors={errors}
            touched={touched}
            customFieldPropsAreEqual={customFieldPropsAreEqual}
            customLeafFieldNames={customLeafFieldNames}
            customContainerChildrenPropsEqual={customContainerChildrenPropsEqual}
            customContainerValuesEqual={customContainerValuesEqual}
          />
        ))}
    </div>
  );
}

export default React.memo(FieldGroup);
